import React, { Fragment, useEffect, useMemo, useState } from "react";
import { ProjectData, TicketData, LocationDataTruncated, Offer } from "./type";
import CloseIcon from "./images/close-icon-white.svg";
import OneRewardIcon from "./images/one-reward-icon.svg";
import TwoRewardsIcon from "./images/two-rewards-icon.svg";
import { FormikRadioButton } from "../../ui/atoms/formik-radio-button";
import { addIneligibleReasons, formatDateToMMDDYYYY } from "./ApplyRewards";

interface InKindWalletObj {
  "House Account"?: string;
  "inKind Wallet"?: string;
}

const getInkindWalletObj = (projectData: ProjectData) => {
  const inKindWalletObj: InKindWalletObj = {};
  if (projectData?.credit_balance)
    inKindWalletObj["House Account"] = projectData?.credit_balance?.toFixed(2);
  if (projectData?.ik_pass_balance)
    inKindWalletObj["inKind Wallet"] = projectData?.ik_pass_balance?.toFixed(2);
  return inKindWalletObj;
};

const supportedTransactionTypes = (validTypes: any) => {
  let types: string[] = [];

  if (validTypes.validForDineIn) {
    types.push("Dine-in");
  }

  if (validTypes.validForTakeout) {
    types.push("Takeout");
  }

  if (validTypes.validForDelivery) {
    types.push("Delivery");
  }

  return types.join(" / ");
};

const getShorterOfferTimes = (redeemable_days_and_hours: string) => {
  return redeemable_days_and_hours
    ?.replace("Mon", "M")
    ?.replace("Tue", "T")
    ?.replace("Wed", "W")
    ?.replace("Thu", "Th")
    ?.replace("Fri", "F")
    ?.replace("Sat", "S")
    ?.replace("Sun", "Su");
};

const getSubText = (offer: Offer, eligible: boolean) => {
  // Is the offer redeemable for certain transaction types?
  const validTypes = {
    validForDineIn:
      offer.restrict_by_order_type == false || offer.restrict_to_dine_in,
    validForTakeout:
      offer.restrict_by_order_type == false || offer.restrict_to_takeout,
    validForDelivery:
      offer.restrict_by_order_type == false || offer.restrict_to_delivery,
  };

  let label = "";
  const transactionTypes = supportedTransactionTypes(validTypes);

  if (
    !validTypes.validForDineIn ||
    !validTypes.validForTakeout ||
    !validTypes.validForDelivery
  ) {
    label = transactionTypes;
  }

  // Is there a minimum spend? If we have populated the label with transaction types, append the minimum spend
  const minSpendLabel = `$${offer.minimum_spend_amount}+`;
  if (offer.minimum_spend_amount && offer.minimum_spend_required) {
    if (label) {
      label = minSpendLabel + " on " + label;
    } else {
      label = minSpendLabel;
    }
  }

  if (offer.redeemable_days_and_hours) {
    if (label) {
      // We don't want to repeat the word on
      const connective = label.includes(" on ") ? " • " : " on ";
      label =
        getShorterOfferTimes(offer.redeemable_days_and_hours ?? "") +
        connective +
        label;
    } else {
      label = offer.redeemable_days_and_hours ?? "";
    }
  }

  // If the label just equals the redemption types, append an 'only' suffix
  if (label && label == transactionTypes) {
    label = label + " only";
  }

  // Return the concatenated label
  return label;
};

interface EditSavingsProps {
  projectData: ProjectData;
  showEditSavings: boolean;
  hideEditSavings: () => void;
  billTotal: number;
  availableBalance: number;
  isFromManagersWebApp?: boolean;
  ticketData: TicketData;
  locationData: LocationDataTruncated;
}

export const EditSavings: React.FC<EditSavingsProps> = ({
  projectData,
  showEditSavings,
  hideEditSavings,
  billTotal,
  availableBalance,
  isFromManagersWebApp,
  ticketData,
  locationData,
}) => {
  const [shakingOfferId, setShakingOfferId] = useState<number | null>(null);

  const validatedOffers = useMemo(
    () =>
      addIneligibleReasons(
        projectData?.offers || [],
        billTotal,
        ticketData,
        locationData,
        isFromManagersWebApp,
      ),
    [projectData, billTotal, isFromManagersWebApp, ticketData, locationData],
  );
  const inkindWalletObj = getInkindWalletObj(projectData);

  const handleIneligibleOfferClick = (eligible: boolean, offerId: number) => {
    if (!eligible && !isFromManagersWebApp) {
      setShakingOfferId(offerId);
    }
  };

  useEffect(() => {
    if (shakingOfferId !== null) {
      const timer = setTimeout(() => {
        setShakingOfferId(null);
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [shakingOfferId]);

  return (
    <div className={`edit-savings-wrapper ${showEditSavings ? "show" : ""}`}>
      <div className="edit-savings">
        <div className="edit-savings-content">
          <div className="edit-savings__header">
            <h2 className="edit-savings__title">Edit Savings</h2>
            <button
              className="edit-savings__close-button"
              onClick={hideEditSavings}
              type="button"
            >
              <img src={CloseIcon} alt="close icon" />
            </button>
          </div>
          <div className="rewards">
            <div className="rewards-title">Rewards</div>
            <div className="rewards-sub-title">
              Only one reward can be applied to your check
            </div>
            <ul className="rewards-options">
              {validatedOffers.map((offer) => {
                const eligible = !offer?.ineligableReason;
                const subText = getSubText(offer, eligible);
                const displayOfferAmount =
                  offer.claim_details.balance > billTotal
                    ? billTotal
                    : offer.claim_details.balance;
                return (
                  <Fragment key={offer.id}>
                    <li
                      className={`rewards-option ${!eligible || offer?.claim_details?.status === "expired" ? "ineligible-fade" : ""}`}
                      onClick={() =>
                        handleIneligibleOfferClick(eligible, offer.id)
                      }
                    >
                      <FormikRadioButton
                        name="offer_id"
                        value={`${offer.claim_details.offer_claim_id}`}
                        label={
                          <div
                            className={`offer-left ${shakingOfferId === offer.id ? "shake" : ""}`}
                          >
                            <span className="offer-name-text">
                              {offer.name}
                            </span>
                            {offer.restriction_text && (
                              <span
                                className={`offer-sub-text ${eligible ? "" : "ineligible"}`}
                              >
                                {subText}
                              </span>
                            )}
                          </div>
                        }
                        disabled={!eligible && !isFromManagersWebApp}
                      />
                      <div className="offer-right">
                        <span className="offer-amount">
                          - ${displayOfferAmount.toFixed(2)}
                        </span>
                        <span
                          className={`offer-sub-text ${offer?.claim_details?.status === "expired" ? "ineligible" : ""}`}
                        >
                          {(eligible || isFromManagersWebApp) &&
                          offer.claim_details.expires_at
                            ? `Exp. ${formatDateToMMDDYYYY(offer.claim_details.expires_at)}`
                            : ""}
                        </span>
                      </div>
                    </li>
                    <div className="divider" />
                  </Fragment>
                );
              })}
              <li className={"rewards-option"}>
                <FormikRadioButton
                  name="offer_id"
                  value={""}
                  label="Don’t apply an offer"
                />
              </li>
            </ul>
          </div>
          {availableBalance > 0 && (
            <div className="inkind-wallet">
              <div className="inkind-wallet-title">inKind Wallet</div>
              <ul className="inkind-wallet-options">
                <li className="inkind-wallet-option">
                  <div className="inkind-wallet-option-top edit-savings-row">
                    <FormikRadioButton
                      name="inkind_wallet"
                      value="apply"
                      label="Available Balance"
                    />
                    <span>${availableBalance.toFixed(2)}</span>
                  </div>
                  <div className="inkind-wallet-option-bottom">
                    {Object.entries(inkindWalletObj).map(
                      ([key, value], index) => {
                        return (
                          <div className="edit-savings-row" key={key}>
                            <div className="inkind-wallet-option-bottom-left">
                              <img
                                src={
                                  index === 0 ? OneRewardIcon : TwoRewardsIcon
                                }
                                alt="inkind wallet icon"
                              />
                              <span>{key}</span>
                            </div>
                            <div className="inkind-wallet-option-bottom-right">
                              {`$${value}`}
                            </div>
                          </div>
                        );
                      },
                    )}
                  </div>
                </li>
                <div className="divider" />
                <li className="inkind-wallet-option">
                  <FormikRadioButton
                    name="inkind_wallet"
                    value={""}
                    label="Don’t use available Wallet balance"
                  />
                </li>
              </ul>
            </div>
          )}
          <button
            type="button"
            className="review-button"
            onClick={hideEditSavings}
          >
            Review Bill
          </button>
        </div>
      </div>
    </div>
  );
};
